import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import ApiIcon from '@mui/icons-material/Api';
import StorageIcon from '@mui/icons-material/Storage';

import {
  autoMatchColumns,
  createMapping,
  fetchMappings,
  fetchMetadata,
  previewCsv,
} from '@src/api/actions/dashboard/dataIntegrations';
import { useAuth } from '@src/context';
import defaultClient from '@src/api/clients/default';
import paths from '@src/paths';
import { useNavigate } from 'react-router-dom';

const STEPS_WITH_SETUP = ['Connection Type', 'Setup Instructions', 'Details', 'Map Columns', 'Preview & Activate'];
const STEPS_WITHOUT_SETUP = ['Connection Type', 'Details', 'Map Columns', 'Preview & Activate'];

const INGEST_EMAIL = 'data@leadgurus.com';

const CONNECTION_TYPES = [
  {
    value: 'email',
    label: 'Automated Email',
    description: 'Your CRM sends a report on a recurring schedule.',
    icon: <EmailIcon sx={{ fontSize: 40 }} />,
  },
  {
    value: 'api',
    label: 'API Connection',
    description: 'Your system pushes data to our REST API.',
    icon: <ApiIcon sx={{ fontSize: 40 }} />,
  },
  {
    value: 'google_sheet',
    label: 'Google Sheet',
    description: 'We pull data from a shared Google spreadsheet.',
    icon: <StorageIcon sx={{ fontSize: 40 }} />,
  },
];

// ─── Step 1: Choose Connection Type ────────────────────────────────

function StepConnectionType({ value, onChange }) {
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        How does your team send backend data?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose the method that best matches your current workflow.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {CONNECTION_TYPES.map((type) => (
          <Card
            key={type.value}
            variant={value === type.value ? 'elevation' : 'outlined'}
            sx={{
              flex: 1,
              border: value === type.value ? '2px solid' : undefined,
              borderColor: value === type.value ? 'primary.main' : undefined,
            }}
          >
            <CardActionArea onClick={() => onChange(type.value)} sx={{ p: 3, textAlign: 'center' }}>
              {type.icon}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>
                {type.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {type.description}
              </Typography>
            </CardActionArea>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}

// ─── Step 1b: Setup Instructions (email only) ─────────────────────

function StepSetupInstructions({ confirmed, onConfirm }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(INGEST_EMAIL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Set up your email automation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Before we configure the connection, set up an automated report in your CRM. Here's what we need:
      </Typography>

      <Stack spacing={2.5}>
        <Box sx={{ p: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            1. Send the report to this email address
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="body1"
              fontWeight={600}
              fontFamily="monospace"
              sx={{ bgcolor: 'white', px: 2, py: 0.75, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
            >
              {INGEST_EMAIL}
            </Typography>
            <IconButton size="small" onClick={handleCopy} color={copied ? 'success' : 'default'}>
              {copied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
            {copied && (
              <Typography variant="caption" color="success.main">Copied!</Typography>
            )}
          </Stack>
        </Box>

        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            2. Use a consistent subject line
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Keep the same subject line every time (e.g., "Daily Lead Report"). We use this to identify your emails automatically.
          </Typography>
        </Box>

        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            3. Use all-time data (strongly recommended)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Set your report to include <strong>all-time data</strong>, not just the last 7 or 30 days.
            Leads generated months ago may convert today — all-time ensures we capture everything.
            Minimum: last 90 days.
          </Typography>
        </Box>

        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            4. Schedule it daily
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Set a daily schedule at a consistent time (e.g., 5:00 AM). We'll check for new data shortly after.
          </Typography>
        </Box>

        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            5. Include as many columns as possible
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The more data you include, the better we can track your full funnel — from lead to appointment to revenue.
            This powers your ROI reporting and helps us optimize your campaigns for the outcomes that matter most.
            You'll map everything in the next step.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            <Chip label="Lead / Prospect ID" size="small" color="primary" variant="filled" />
            <Chip label="Created Date" size="small" color="primary" variant="filled" />
            <Chip label="Phone Number" size="small" color="primary" variant="filled" />
            <Chip label="Email Address" size="small" color="primary" variant="filled" />
            <Chip label="Appointment Date" size="small" color="success" variant="outlined" />
            <Chip label="Sold / Close Date" size="small" color="success" variant="outlined" />
            <Chip label="Gross Revenue" size="small" color="success" variant="outlined" />
            <Chip label="Net Revenue" size="small" color="primary" variant="filled" />
            <Chip label="ZIP Code" size="small" color="success" variant="outlined" />
            <Chip label="Demo (Yes/No)" size="small" variant="outlined" />
            <Chip label="Issue (Yes/No)" size="small" variant="outlined" />
            <Chip label="Status / Stage" size="small" variant="outlined" />
          </Box>
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              <Chip label="" size="small" color="primary" variant="filled" sx={{ width: 12, height: 12, mr: 0.5 }} /> Required
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <Chip label="" size="small" color="success" variant="outlined" sx={{ width: 12, height: 12, mr: 0.5 }} /> Recommended
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <Chip label="" size="small" variant="outlined" sx={{ width: 12, height: 12, mr: 0.5 }} /> Optional
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            6. Use CSV format
          </Typography>
          <Typography variant="body2" color="text.secondary">
            CSV or Excel (.xlsx) attachments work best. Avoid PDF or embedded HTML tables.
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ my: 3 }} />

      <FormControlLabel
        control={
          <Checkbox
            checked={confirmed}
            onChange={(e) => onConfirm(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Typography variant="body2">
            I've set up the email automation and sent at least one test email to <strong>{INGEST_EMAIL}</strong>
          </Typography>
        }
      />
    </Box>
  );
}

// ─── Step 2: Connection Details ────────────────────────────────────

function StepDetails({ type, config, onChange, clientOptions, selectedClient, onClientChange, isSingleClient }) {
  const update = (field) => (e) => onChange({ ...config, [field]: e.target.value });

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {type === 'email'
          ? 'Tell us about the email you set up'
          : type === 'api'
            ? 'Configure your API connection'
            : 'Connect your Google Sheet'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {type === 'email'
          ? 'Enter the details of the automated email you configured so we can find and process it.'
          : type === 'api'
            ? 'Provide the endpoint and credentials so we can pull your data.'
            : 'Share your spreadsheet so we can read your data.'}
      </Typography>

      <Stack spacing={2} sx={{ maxWidth: 500 }}>
        {isSingleClient ? (
          <TextField
            label="Client"
            value={selectedClient?.name || ''}
            disabled
            helperText="Your account is linked to this client."
          />
        ) : (
          <Autocomplete
            options={clientOptions}
            getOptionLabel={(o) => o.name || ''}
            value={selectedClient}
            onChange={(_, v) => onClientChange(v)}
            isOptionEqualToValue={(option, val) => option.id === val?.id}
            renderInput={(params) => <TextField {...params} label="Client" required />}
          />
        )}

        {type === 'email' && (
          <>
            <TextField
              label="Sender Email Address"
              placeholder="reports@theircrm.com"
              value={config.email_sender || ''}
              onChange={update('email_sender')}
              required
              helperText="The 'From' address on the automated email."
            />
            <TextField
              label="Email Subject Line"
              placeholder="Daily Lead Report"
              value={config.subject || ''}
              onChange={update('subject')}
              required
              helperText="Must match exactly — copy and paste from the email you received."
            />
            <TextField
              label="Attachment Filename Contains"
              placeholder="leads"
              value={config.filename_substring || ''}
              onChange={update('filename_substring')}
              helperText="Optional: helps us pick the right attachment if the email has multiple files."
            />

            <Divider />
            <Typography variant="subtitle2">Schedule</Typography>
            <Typography variant="body2" color="text.secondary">
              Set this to run shortly after your CRM sends the email. For example, if your CRM sends at 5:00 AM, set this to 5:15 AM.
            </Typography>
          </>
        )}

        {type === 'api' && (
          <>
            <TextField
              label="API Endpoint URL"
              placeholder="https://api.example.com/leads"
              value={config.api_endpoint || ''}
              onChange={update('api_endpoint')}
              required
              helperText="The URL we'll call to fetch your data."
            />
            <TextField
              label="API Key"
              placeholder="Your API key"
              value={config.api_key || ''}
              onChange={update('api_key')}
              type="password"
              helperText="Stored securely. We'll include this in the request header."
            />
            <Divider />
            <Typography variant="subtitle2">Schedule</Typography>
          </>
        )}

        {type === 'google_sheet' && (
          <>
            <TextField
              label="Google Sheet URL or ID"
              placeholder="https://docs.google.com/spreadsheets/d/..."
              value={config.spreadsheet_id || ''}
              onChange={update('spreadsheet_id')}
              required
              helperText="Paste the full URL or just the spreadsheet ID. Make sure it's shared with our service account."
            />
            <TextField
              label="Sheet & Range"
              placeholder="Sheet1!A:Z"
              value={config.worksheet_range || ''}
              onChange={update('worksheet_range')}
              helperText="Which sheet tab and columns to read (e.g., Sheet1!A:Z)."
            />
            <Divider />
            <Typography variant="subtitle2">Schedule</Typography>
          </>
        )}

        <FormControl>
          <InputLabel>Frequency</InputLabel>
          <Select
            value={config.schedule_type || 'daily'}
            onChange={update('schedule_type')}
            label="Frequency"
          >
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="hourly">Every Hour</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Run Time"
          type="time"
          value={config.run_time || '06:00'}
          onChange={update('run_time')}
          helperText={type === 'email'
            ? 'Set this 10–15 minutes after your CRM sends the email.'
            : 'When should we pull new data?'}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
    </Box>
  );
}

// ─── Step 3: Column Mapping ────────────────────────────────────────

const REQUIRED_SET = new Set(['prospect_id', 'created_date', 'net_amount']);
const CONTACT_SET = new Set(['phone', 'email']);
const BOOLEAN_FIELDS = ['demo', 'issue'];
const RECOMMENDED_FIELDS = ['prospect_id', 'created_date', 'phone', 'email', 'appointment_date', 'sold_date', 'gross_amount', 'net_amount', 'zip_code', 'demo', 'issue'];

const BOOL_RULE_TYPES = [
  { value: '', label: 'Not applicable' },
  { value: 'contains', label: 'When a column contains a value' },
  { value: 'not_empty', label: 'When a column is not empty' },
];

const DATE_FIELDS = ['appointment_date', 'sold_date'];

const DATE_RULE_TYPES = [
  { value: '', label: 'Leave empty (not available)' },
  { value: 'copy_mapped', label: 'Always copy from a mapped field' },
  { value: 'copy_csv', label: 'Copy from a CSV column (with optional condition)' },
];

const CONDITION_OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_empty', label: 'Is not empty' },
];

function StepColumnMapping({
  mappings,
  onMappingsChange,
  headers,
  onFileUpload,
  backendFields,
  fieldLabels,
  uploading,
  suggestions,
  transforms,
  onTransformsChange,
  sampleValues,
}) {
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) onFileUpload(file);
  };

  const usedFields = useMemo(() => {
    const used = new Set();
    for (const field of Object.values(mappings)) {
      if (field) used.add(field);
    }
    return used;
  }, [mappings]);

  const hasContact = usedFields.has('phone') || usedFields.has('email');
  const missingRequired = [...REQUIRED_SET].filter((f) => !usedFields.has(f));

  const unmappedDateFields = useMemo(() =>
    DATE_FIELDS.filter((f) => !usedFields.has(f)),
    [usedFields]
  );

  // Mapped backend fields that can serve as date sources
  const mappedDateSources = useMemo(() =>
    ['created_date', 'appointment_date', 'sold_date'].filter((f) => usedFields.has(f)),
    [usedFields]
  );

  const unmappedBoolFields = useMemo(() =>
    BOOLEAN_FIELDS.filter((f) => !usedFields.has(f)),
    [usedFields]
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Map your columns to our fields
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload a sample file from your report. We'll auto-detect your columns and suggest the best matches.
        The more fields you map, the richer your ROI reporting will be.
      </Typography>

      <Button
        variant="outlined"
        startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        sx={{ mb: 3 }}
      >
        {headers.length ? 'Re-upload Sample File' : 'Upload Sample CSV'}
      </Button>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        hidden
        onChange={handleFile}
      />

      {headers.length > 0 && (
        <>
          {/* Field coverage chips */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Field Coverage
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {RECOMMENDED_FIELDS.map((field) => (
                <Chip
                  key={field}
                  label={fieldLabels[field] || field}
                  size="small"
                  color={usedFields.has(field) ? 'success' : (REQUIRED_SET.has(field) || CONTACT_SET.has(field)) ? 'error' : 'default'}
                  variant={usedFields.has(field) ? 'filled' : 'outlined'}
                />
              ))}
            </Stack>
          </Box>

          {missingRequired.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Required: {missingRequired.map((f) => fieldLabels[f] || f).join(', ')}
            </Alert>
          )}
          {!hasContact && (
            <Alert severity="error" sx={{ mb: 2 }}>
              At least one contact field (Phone or Email) must be mapped.
            </Alert>
          )}

          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Your CSV Column</TableCell>
                  <TableCell>Maps To</TableCell>
                  <TableCell width={80}>Match</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {headers.map((header) => {
                  const current = mappings[header] || '';
                  const suggestion = suggestions?.suggestions?.[header];
                  const confidence = suggestion?.confidence;

                  return (
                    <TableRow key={header}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {header}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={current}
                            onChange={(e) =>
                              onMappingsChange({ ...mappings, [header]: e.target.value })
                            }
                            displayEmpty
                          >
                            <MenuItem value="">
                              <em>-- Skip --</em>
                            </MenuItem>
                            {backendFields.map((field) => {
                              const taken = usedFields.has(field) && field !== current;
                              return (
                                <MenuItem key={field} value={field} disabled={taken}>
                                  {fieldLabels[field] || field}
                                  {taken ? ' (already mapped)' : ''}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        {confidence != null && (
                          <Chip
                            label={`${Math.round(confidence * 100)}%`}
                            size="small"
                            color={confidence >= 0.9 ? 'success' : confidence >= 0.75 ? 'warning' : 'default'}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Date field rules for unmapped appointment_date / sold_date */}
          {unmappedDateFields.length > 0 && (
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Missing date fields
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your data doesn't have columns mapped for these fields. Copy from another field, optionally with a condition.
              </Typography>
              <Stack spacing={3}>
                {unmappedDateFields.map((field) => {
                  const rule = transforms[`date_${field}`] || {};
                  const ruleType = rule.type || '';

                  return (
                    <Box key={field}>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                        {fieldLabels[field] || field}
                      </Typography>
                      <Stack spacing={1.5}>
                        {/* Rule type */}
                        <FormControl size="small" sx={{ maxWidth: 400 }}>
                          <InputLabel>How to populate</InputLabel>
                          <Select
                            value={ruleType}
                            onChange={(e) => onTransformsChange({
                              ...transforms,
                              [`date_${field}`]: { type: e.target.value },
                            })}
                            label="How to populate"
                          >
                            {DATE_RULE_TYPES.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Copy from mapped field (always) */}
                        {ruleType === 'copy_mapped' && (
                          <FormControl size="small" sx={{ maxWidth: 400 }}>
                            <InputLabel>Source field</InputLabel>
                            <Select
                              value={rule.source || ''}
                              onChange={(e) => onTransformsChange({
                                ...transforms,
                                [`date_${field}`]: { ...rule, source: e.target.value },
                              })}
                              label="Source field"
                            >
                              {mappedDateSources
                                .filter((f) => f !== field)
                                .map((f) => (
                                  <MenuItem key={f} value={f}>{fieldLabels[f] || f}</MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        )}

                        {/* Copy from CSV column with optional condition */}
                        {ruleType === 'copy_csv' && (
                          <>
                            <FormControl size="small" sx={{ maxWidth: 400 }}>
                              <InputLabel>Copy date from CSV column</InputLabel>
                              <Select
                                value={rule.sourceColumn || ''}
                                onChange={(e) => onTransformsChange({
                                  ...transforms,
                                  [`date_${field}`]: { ...rule, sourceColumn: e.target.value },
                                })}
                                label="Copy date from CSV column"
                              >
                                {headers.map((h) => (
                                  <MenuItem key={h} value={h}>{h}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Condition (optional) — only set when:
                            </Typography>

                            <Stack direction="row" spacing={1} alignItems="center">
                              <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>CSV column</InputLabel>
                                <Select
                                  value={rule.condColumn || ''}
                                  onChange={(e) => onTransformsChange({
                                    ...transforms,
                                    [`date_${field}`]: { ...rule, condColumn: e.target.value, condOp: rule.condOp || 'equals' },
                                  })}
                                  label="CSV column"
                                >
                                  <MenuItem value=""><em>No condition</em></MenuItem>
                                  {headers.map((h) => (
                                    <MenuItem key={h} value={h}>{h}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>

                              {rule.condColumn && (
                                <FormControl size="small" sx={{ minWidth: 130 }}>
                                  <InputLabel>Operator</InputLabel>
                                  <Select
                                    value={rule.condOp || 'equals'}
                                    onChange={(e) => onTransformsChange({
                                      ...transforms,
                                      [`date_${field}`]: { ...rule, condOp: e.target.value },
                                    })}
                                    label="Operator"
                                  >
                                    {CONDITION_OPERATORS.map((op) => (
                                      <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}

                              {rule.condColumn && rule.condOp !== 'not_empty' && (
                                <TextField
                                  size="small"
                                  label="Value"
                                  value={rule.condValue || ''}
                                  onChange={(e) => onTransformsChange({
                                    ...transforms,
                                    [`date_${field}`]: { ...rule, condValue: e.target.value },
                                  })}
                                  sx={{ minWidth: 120 }}
                                />
                              )}
                            </Stack>

                            {rule.sourceColumn && (
                              <Typography variant="caption" color="text.secondary">
                                {rule.condColumn
                                  ? `Set ${fieldLabels[field] || field} = "${rule.sourceColumn}" when "${rule.condColumn}" ${rule.condOp === 'not_empty' ? 'has any value' : `${rule.condOp || 'equals'} "${rule.condValue || ''}"`}`
                                  : `Always set ${fieldLabels[field] || field} from "${rule.sourceColumn}"`
                                }
                              </Typography>
                            )}
                          </>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}

          {/* Boolean field rules for unmapped demo/issue */}
          {unmappedBoolFields.length > 0 && (
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Outcome fields (Demo / Issue)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your data doesn't have a direct column for these. Set a rule to determine them from other columns.
              </Typography>
              <Stack spacing={3}>
                {unmappedBoolFields.map((field) => {
                  const rule = transforms[`bool_${field}`] || {};
                  const ruleType = rule.type || '';
                  const selectedValues = rule.values || [];
                  const columnValues = (rule.column && sampleValues?.[rule.column]) || [];

                  return (
                    <Box key={field}>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                        {fieldLabels[field] || field}
                      </Typography>
                      <Stack spacing={1.5}>
                        <FormControl size="small" sx={{ maxWidth: 350 }}>
                          <InputLabel>Rule type</InputLabel>
                          <Select
                            value={ruleType}
                            onChange={(e) => onTransformsChange({
                              ...transforms,
                              [`bool_${field}`]: { type: e.target.value, column: '', values: [] },
                            })}
                            label="Rule type"
                          >
                            {BOOL_RULE_TYPES.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {(ruleType === 'contains' || ruleType === 'not_empty') && (
                          <FormControl size="small" sx={{ maxWidth: 350 }}>
                            <InputLabel>CSV Column</InputLabel>
                            <Select
                              value={rule.column || ''}
                              onChange={(e) => onTransformsChange({
                                ...transforms,
                                [`bool_${field}`]: { ...rule, column: e.target.value, values: [] },
                              })}
                              label="CSV Column"
                            >
                              {headers.map((h) => (
                                <MenuItem key={h} value={h}>{h}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}

                        {ruleType === 'contains' && rule.column && (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Select which values mean "{fieldLabels[field] || field}":
                            </Typography>
                            {columnValues.length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                {columnValues.map((val) => {
                                  const isSelected = selectedValues.includes(val);
                                  return (
                                    <Chip
                                      key={val}
                                      label={val}
                                      size="small"
                                      variant={isSelected ? 'filled' : 'outlined'}
                                      color={isSelected ? 'primary' : 'default'}
                                      onClick={() => {
                                        const next = isSelected
                                          ? selectedValues.filter((v) => v !== val)
                                          : [...selectedValues, val];
                                        onTransformsChange({
                                          ...transforms,
                                          [`bool_${field}`]: { ...rule, values: next },
                                        });
                                      }}
                                      sx={{ cursor: 'pointer' }}
                                    />
                                  );
                                })}
                              </Box>
                            ) : (
                              <TextField
                                size="small"
                                label="Values (comma separated)"
                                placeholder='e.g. Demo, Demo Sold, Appt Confirmed'
                                value={selectedValues.join(', ')}
                                onChange={(e) => {
                                  const vals = e.target.value
                                    .split(',')
                                    .map((v) => v.trim())
                                    .filter(Boolean);
                                  onTransformsChange({
                                    ...transforms,
                                    [`bool_${field}`]: { ...rule, values: vals },
                                  });
                                }}
                                sx={{ maxWidth: 400 }}
                              />
                            )}
                            {selectedValues.length > 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {selectedValues.length} value{selectedValues.length !== 1 ? 's' : ''} selected
                              </Typography>
                            )}
                          </Box>
                        )}

                        {ruleType === 'not_empty' && rule.column && (
                          <Typography variant="body2" color="text.secondary">
                            {fieldLabels[field] || field} will be set to Yes when "{rule.column}" has any value.
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

// ─── Step 4: Preview & Activate ────────────────────────────────────

function StepPreview({ previewData, loading, error, onRunPreview }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Running preview...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!previewData) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Almost there — let's verify everything looks right
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          We'll process your sample file with the mappings you configured and show you
          exactly how the data will appear. If something looks off, you can go back and adjust.
        </Typography>
        <Button variant="contained" onClick={onRunPreview}>
          Run Preview
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Preview results
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review the parsed data below. If everything looks correct, activate the connection — we'll start importing automatically on schedule.
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Card variant="outlined" sx={{ flex: 1, p: 2, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={700}>
            {previewData.total_rows}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total Rows
          </Typography>
        </Card>
        <Card variant="outlined" sx={{ flex: 1, p: 2, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={700} color="success.main">
            {previewData.success_count}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Parsed OK
          </Typography>
        </Card>
        <Card variant="outlined" sx={{ flex: 1, p: 2, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={700} color="error.main">
            {previewData.error_count}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Errors
          </Typography>
        </Card>
      </Stack>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Sample Rows (first {previewData.preview_rows.length})
      </Typography>
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              {Object.keys(previewData.preview_rows[0]?.parsed || previewData.preview_rows[0]?.raw || {})
                .slice(0, 8)
                .map((key) => (
                  <TableCell key={key}>{key}</TableCell>
                ))}
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {previewData.preview_rows.map((row) => {
              const displayData = row.parsed || row.raw;
              const keys = Object.keys(displayData || {}).slice(0, 8);
              return (
                <TableRow key={row.row_number}>
                  <TableCell>{row.row_number}</TableCell>
                  {keys.map((key) => (
                    <TableCell key={key} sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {String(displayData[key] ?? '')}
                    </TableCell>
                  ))}
                  <TableCell>
                    {row.error ? (
                      <Chip label="Error" color="error" size="small" />
                    ) : (
                      <Chip label="OK" color="success" size="small" />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── Main Wizard ───────────────────────────────────────────────────

export default function DataIntegrationWizardPage() {
  const navigate = useNavigate();
  const { state } = useAuth();
  const user = state?.user;

  // ── Resolve accessible clients from membership context ──
  const [clientOptions, setClientOptions] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    if (!user) return;

    // Always fetch the user's accessible clients from the API.
    // The endpoint is scoped by limit_to_user_clients so non-staff
    // users only see their own clients (direct + parent-company children).
    defaultClient
      .request({ url: 'dashboard/clients/?fields=id,name,slug&page_size=500', method: 'GET' })
      .then((res) => {
        const rows = Array.isArray(res?.data?.results)
          ? res.data.results
          : Array.isArray(res?.data) ? res.data : [];
        setClientOptions(rows);
        // Auto-select when there's exactly one client
        if (rows.length === 1) setSelectedClient(rows[0]);
      })
      .catch(() => setClientOptions([]));
  }, [user]);

  const isStaff = user?.is_staff || user?.is_superuser;
  const isSingleClient = !isStaff && clientOptions.length === 1 && selectedClient;

  const [activeStep, setActiveStep] = useState(0);
  const [connectionType, setConnectionType] = useState('');
  const [setupConfirmed, setSetupConfirmed] = useState(false);
  const [config, setConfig] = useState({ schedule_type: 'daily', run_time: '06:00' });

  // Email gets a setup instructions step; others go straight to details
  const hasSetupStep = connectionType === 'email';
  const steps = hasSetupStep ? STEPS_WITH_SETUP : STEPS_WITHOUT_SETUP;
  // Logical step indices (offset by 1 when setup step is present)
  const STEP_SETUP = hasSetupStep ? 1 : -1;
  const STEP_DETAILS = hasSetupStep ? 2 : 1;
  const STEP_COLUMNS = hasSetupStep ? 3 : 2;
  const STEP_PREVIEW = hasSetupStep ? 4 : 3;
  const [columnMappings, setColumnMappings] = useState({});
  const [dateTransforms, setDateTransforms] = useState({});
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [sampleValues, setSampleValues] = useState({});
  const [suggestions, setSuggestions] = useState(null);
  const [sampleFile, setSampleFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    fetchMetadata()
      .then((res) => setMetadata(res.data))
      .catch(() => {});
  }, []);

  const backendFields = metadata?.backend_fields || [];
  const fieldLabels = metadata?.field_labels || {};

  const handleFileUpload = useCallback(
    async (file) => {
      setUploading(true);
      setSampleFile(file);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await autoMatchColumns(formData);
        const data = res.data;
        setCsvHeaders(
          Object.keys(data.suggestions || {}).concat(data.unmapped || [])
        );
        setSuggestions(data);
        setSampleValues(data.sample_values || {});

        // Auto-populate column mappings from suggestions
        const auto = {};
        for (const [header, info] of Object.entries(data.suggestions || {})) {
          if (info.field) auto[header] = info.field;
        }
        setColumnMappings(auto);
      } catch (err) {
        console.error('Auto-match failed:', err);
      } finally {
        setUploading(false);
      }
    },
    []
  );

  const handleRunPreview = useCallback(async () => {
    if (!sampleFile) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      // Build clean column_mappings (skip empty values)
      const cleanMappings = {};
      for (const [header, field] of Object.entries(columnMappings)) {
        if (field) cleanMappings[header] = field;
      }

      const formData = new FormData();
      formData.append('file', sampleFile);
      formData.append('column_mappings', JSON.stringify(cleanMappings));

      const res = await previewCsv(formData);
      setPreviewData(res.data);
    } catch (err) {
      setPreviewError(err?.response?.data?.detail || err?.message || 'Preview failed.');
    } finally {
      setPreviewLoading(false);
    }
  }, [sampleFile, columnMappings]);

  const handleActivate = useCallback(async () => {
    setSaving(true);
    setSaveError(null);

    const cleanMappings = {};
    for (const [header, field] of Object.entries(columnMappings)) {
      if (field) cleanMappings[header] = field;
    }

    // Build transform_params from date rules + boolean rules
    const transformations = [];
    for (const [key, value] of Object.entries(dateTransforms)) {
      if (key.startsWith('bool_')) {
        // Boolean field rule (demo/issue)
        const field = key.replace('bool_', '');
        const rule = value;
        if (!rule.type || !rule.column) continue;

        if (rule.type === 'contains' && rule.values?.length) {
          for (const matchVal of rule.values) {
            transformations.push({
              set: field,
              to: true,
              when: { all: [{ field: [rule.column], eq: matchVal }] },
            });
          }
        } else if (rule.type === 'not_empty') {
          transformations.push({
            set: field,
            to: true,
            when: { all: [{ field: [rule.column], not_empty: true }] },
          });
        }
      } else if (key.startsWith('date_')) {
        // Date field rule (appointment_date/sold_date)
        const field = key.replace('date_', '');
        const rule = value;
        if (!rule.type) continue;

        if (rule.type === 'copy_mapped' && rule.source) {
          // Always copy from another mapped backend field
          transformations.push({
            set: field,
            to: { field: [rule.source] },
          });
        } else if (rule.type === 'copy_csv' && rule.sourceColumn) {
          // Copy from a CSV column, optionally with a condition
          const entry = {
            set: field,
            to: { field: [rule.sourceColumn] },
          };
          if (rule.condColumn) {
            if (rule.condOp === 'not_empty') {
              entry.when = { all: [{ field: [rule.condColumn], not_empty: true }] };
            } else if (rule.condOp === 'contains') {
              entry.when = { all: [{ field: [rule.condColumn], contains: rule.condValue }] };
            } else {
              // equals (default)
              entry.when = { all: [{ field: [rule.condColumn], eq: rule.condValue }] };
            }
          }
          transformations.push(entry);
        }
      }
    }
    const transformParams = transformations.length > 0 ? { transformations } : {};

    const payload = {
      client: selectedClient?.id,
      schedule_type: config.schedule_type || 'daily',
      run_time: config.run_time || '06:00',
      column_mappings: cleanMappings,
      transform_params: transformParams,
    };

    if (connectionType === 'email') {
      payload.email_sender = config.email_sender;
      payload.subject = config.subject;
      payload.filename_substring = config.filename_substring || '';
    } else if (connectionType === 'api') {
      payload.api_endpoint = config.api_endpoint;
      payload.api_key = config.api_key || '';
    } else if (connectionType === 'google_sheet') {
      payload.spreadsheet_id = config.spreadsheet_id;
      payload.worksheet_range = config.worksheet_range || '';
    }

    try {
      await createMapping(connectionType, payload);
      navigate(paths.dataIntegrations.health);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (typeof err?.response?.data === 'object'
          ? JSON.stringify(err.response.data)
          : err?.message || 'Failed to create integration.');
      setSaveError(detail);
    } finally {
      setSaving(false);
    }
  }, [connectionType, config, columnMappings, dateTransforms, selectedClient, navigate]);

  const canProceed = useMemo(() => {
    if (activeStep === 0) return !!connectionType;
    if (activeStep === STEP_SETUP) return setupConfirmed;
    if (activeStep === STEP_DETAILS) {
      if (!selectedClient) return false;
      if (connectionType === 'email') return !!(config.email_sender && config.subject);
      if (connectionType === 'api') return !!config.api_endpoint;
      if (connectionType === 'google_sheet') return !!config.spreadsheet_id;
      return true;
    }
    if (activeStep === STEP_COLUMNS) {
      const mapped = new Set(Object.values(columnMappings).filter(Boolean));
      const hasRequired = mapped.has('prospect_id') && mapped.has('created_date') && mapped.has('net_amount');
      const hasContact = mapped.has('phone') || mapped.has('email');
      return hasRequired && hasContact;
    }
    return true;
  }, [activeStep, connectionType, config, selectedClient, columnMappings, setupConfirmed, STEP_SETUP, STEP_DETAILS, STEP_COLUMNS]);

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(paths.dataIntegrations.health)}
        sx={{ mb: 2 }}
      >
        Back to Integrations
      </Button>

      <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
        Connect Your Data
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Set up a connection to automatically import your backend data.
      </Typography>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        {activeStep === 0 && (
          <StepConnectionType value={connectionType} onChange={setConnectionType} />
        )}
        {activeStep === STEP_SETUP && (
          <StepSetupInstructions
            confirmed={setupConfirmed}
            onConfirm={setSetupConfirmed}
          />
        )}
        {activeStep === STEP_DETAILS && (
          <StepDetails
            type={connectionType}
            config={config}
            onChange={setConfig}
            clientOptions={clientOptions || []}
            selectedClient={selectedClient}
            onClientChange={setSelectedClient}
            isSingleClient={isSingleClient}
          />
        )}
        {activeStep === STEP_COLUMNS && (
          <StepColumnMapping
            mappings={columnMappings}
            onMappingsChange={setColumnMappings}
            headers={csvHeaders}
            onFileUpload={handleFileUpload}
            backendFields={backendFields}
            fieldLabels={fieldLabels}
            uploading={uploading}
            suggestions={suggestions}
            transforms={dateTransforms}
            onTransformsChange={setDateTransforms}
            sampleValues={sampleValues}
          />
        )}
        {activeStep === STEP_PREVIEW && (
          <StepPreview
            previewData={previewData}
            loading={previewLoading}
            error={previewError}
            onRunPreview={handleRunPreview}
          />
        )}
      </Card>

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}

      <Stack direction="row" justifyContent="space-between">
        <Button
          disabled={activeStep === 0}
          onClick={() => setActiveStep((s) => s - 1)}
        >
          Back
        </Button>
        <Box>
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              disabled={!canProceed}
              onClick={() => setActiveStep((s) => s + 1)}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              startIcon={saving ? <CircularProgress size={16} /> : <CheckCircleIcon />}
              disabled={saving}
              onClick={handleActivate}
            >
              {saving ? 'Activating...' : 'Activate Connection'}
            </Button>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
